import React, { useState } from 'react';
import { useBillingState, openCustomerPortal, syncBillingStatus, startCheckout } from '../lib/billing';

// ─── BillingSettingsTab ─────────────────────────────────────────────────────
//
// Settings → Billing. Shown to the company's billing admin; other operators
// see "Contact your billing admin" copy. The billing-admin gate uses
// auth.uid() === billing_admin_user_id; if billing_admin_user_id is null
// (no one has subscribed yet) we show the tab so the first operator can
// kick off checkout.
//
// Dev-mode case (companies 4 + 10 backfilled active without a Stripe
// customer): "Manage subscription" returns `no_subscription_yet`. The
// component re-routes that into a friendly "this account predates billing
// setup" CTA that fires create-checkout-session — exactly the UX Austin
// asked for in the plan callout. Don't lock dev-mode accounts out of
// upgrading.

const PLAN_LABELS = {
  monthly: 'Monthly · $39/mo',
  annual: 'Annual · $390/yr',
};

const STATUS_PALETTE = {
  trialing: { bg: '#FEF3C7', fg: '#92400E', label: 'Trialing' },
  active: { bg: '#D1FAE5', fg: '#065F46', label: 'Active' },
  past_due: { bg: '#FED7AA', fg: '#9A3412', label: 'Past due' },
  unpaid: { bg: '#FEE2E2', fg: '#991B1B', label: 'Unpaid' },
  canceled: { bg: '#E5E7EB', fg: '#374151', label: 'Canceled' },
  incomplete: { bg: '#FEF3C7', fg: '#92400E', label: 'Incomplete' },
  incomplete_expired: { bg: '#FEE2E2', fg: '#991B1B', label: 'Incomplete' },
};

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function BillingSettingsTab({ companyId, currentUserId, billingAdminUserId, isMobile }) {
  const { state, loading, refetch } = useBillingState(companyId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Permissions: show the tab when the operator IS the billing admin OR
  // there's no admin yet (first-subscriber path).
  const isBillingAdmin = !billingAdminUserId || (currentUserId && billingAdminUserId === currentUserId);

  if (!isBillingAdmin) {
    return (
      <div style={{
        background: '#FFF', borderRadius: 14, padding: 22,
        border: '1px solid #EDEDED', maxWidth: 520,
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#111', marginBottom: 6 }}>
          Billing managed by another team member
        </div>
        <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>
          Contact your billing admin to make changes to the subscription.
        </div>
      </div>
    );
  }

  if (loading || !state) {
    return (
      <div style={{ fontSize: 14, color: '#9CA3AF' }}>{'Loading billing status…'}</div>
    );
  }

  const status = state.subscription_status || 'inactive';
  const palette = STATUS_PALETTE[status] || { bg: '#E5E7EB', fg: '#374151', label: status };
  const isDevMode = state.subscription_status === 'active' && !state.stripe_customer_id;

  async function handleManage() {
    if (busy) return;
    setBusy(true);
    setError('');
    await openCustomerPortal({
      onError: (r) => {
        if (r && r.error === 'no_subscription_yet') {
          // Dev-mode: surface the friendly upgrade CTA inline rather
          // than redirecting. Operator sees the inline panel below.
          setError('');
          return;
        }
        setError((r && r.body && r.body.message) || 'Couldn\'t open billing portal.');
      },
    });
    setBusy(false);
  }

  async function handleStartSubscription(plan) {
    if (busy) return;
    setBusy(true);
    setError('');
    await startCheckout(plan, {
      onError: (r) => setError((r && r.body && r.body.message) || 'Couldn\'t start checkout.'),
    });
    setBusy(false);
  }

  async function handleSync() {
    if (busy) return;
    setBusy(true);
    setError('');
    const r = await syncBillingStatus();
    if (!r.ok) {
      setError((r && r.body && r.body.message) || 'Sync failed.');
    } else {
      await refetch();
    }
    setBusy(false);
  }

  const cardStyle = {
    background: '#FFF', borderRadius: 14, padding: isMobile ? 18 : 22,
    border: '1px solid #EDEDED', marginBottom: 16,
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em' };
  const rowStyle = { display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6, flexWrap: 'wrap' };

  return (
    <div style={{ animation: 'fadeUp 0.25s ease' }}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#111' }}>Subscription</div>
          <span style={{
            display: 'inline-block', padding: '4px 12px', borderRadius: 999,
            background: palette.bg, color: palette.fg,
            fontSize: 12, fontWeight: 600, letterSpacing: '0.02em',
          }}>{palette.label}</span>
        </div>

        <div style={rowStyle}>
          <span style={{ ...labelStyle, width: 120 }}>Plan</span>
          <span style={{ fontSize: 14, color: '#111' }}>
            {PLAN_LABELS[state.subscription_plan] || (isDevMode ? 'Development access' : '—')}
          </span>
        </div>

        {state.trial_ends_at && status === 'trialing' && (
          <div style={rowStyle}>
            <span style={{ ...labelStyle, width: 120 }}>Trial ends</span>
            <span style={{ fontSize: 14, color: '#111' }}>{fmtDate(state.trial_ends_at)}</span>
          </div>
        )}

        {state.subscription_period_ends_at && (
          <div style={rowStyle}>
            <span style={{ ...labelStyle, width: 120 }}>
              {status === 'canceled' ? 'Access until' : 'Next charge'}
            </span>
            <span style={{ fontSize: 14, color: '#111' }}>
              {fmtDate(state.subscription_period_ends_at)}
            </span>
          </div>
        )}

        {error && (
          <div style={{
            marginTop: 12, fontSize: 12, color: '#B91C1C',
            background: '#FEE2E2', borderRadius: 8, padding: '8px 12px',
          }}>
            {error}
          </div>
        )}

        {/* Dev-mode panel: companies that predate billing setup. */}
        {isDevMode ? (
          <div style={{
            marginTop: 14, padding: 14, borderRadius: 10,
            background: '#F9FAFB', border: '1px solid #E5E7EB',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#111', marginBottom: 4 }}>
              This account was created before billing was set up.
            </div>
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 10, lineHeight: 1.5 }}>
              You have full access while we get billing infrastructure in place.
              When you're ready to start a real subscription, click below.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={busy}
                onClick={() => handleStartSubscription('monthly')}
                style={{
                  padding: '8px 14px', borderRadius: 8, border: 'none',
                  background: '#2563EB', color: '#FFF',
                  fontSize: 13, fontWeight: 600,
                  cursor: busy ? 'wait' : 'pointer',
                }}
              >
                Subscribe Monthly · $39/mo
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => handleStartSubscription('annual')}
                style={{
                  padding: '8px 14px', borderRadius: 8, border: '1px solid #111',
                  background: 'transparent', color: '#111',
                  fontSize: 13, fontWeight: 600,
                  cursor: busy ? 'wait' : 'pointer',
                }}
              >
                Subscribe Annually · $390/yr
              </button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={busy}
              onClick={handleManage}
              style={{
                padding: '10px 18px', borderRadius: 10, border: 'none',
                background: '#111', color: '#FFF',
                fontSize: 13, fontWeight: 600,
                cursor: busy ? 'wait' : 'pointer',
              }}
            >
              {busy ? 'Opening…' : 'Manage subscription'}
            </button>
          </div>
        )}
      </div>

      {/* Issues with billing? — expandable advanced controls */}
      <div style={{
        background: '#FFF', borderRadius: 14, padding: 16,
        border: '1px solid #EDEDED',
      }}>
        <button
          type="button"
          onClick={() => setShowAdvanced(s => !s)}
          style={{
            background: 'transparent', border: 'none', padding: 0,
            color: '#6B7280', fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          {showAdvanced ? '▾' : '▸'} Issues with billing?
        </button>
        {showAdvanced && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#6B7280', lineHeight: 1.5 }}>
            If your subscription state in Posted looks out of date relative to Stripe,
            click below to pull the latest from Stripe and update Posted.
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                disabled={busy}
                onClick={handleSync}
                style={{
                  padding: '6px 12px', borderRadius: 8, border: '1px solid #E5E7EB',
                  background: '#FFF', color: '#111',
                  fontSize: 12, fontWeight: 500,
                  cursor: busy ? 'wait' : 'pointer',
                }}
              >
                {busy ? 'Syncing…' : 'Sync billing status'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
